import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, IconButton, Tooltip, Snackbar, Alert,
  CircularProgress, Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_ORDER = ["Asset", "Liability", "Equity", "Income", "Expense"];

const EMPTY = { EntryDate: today(), outAccount: "", inAccount: "", amount: "", memo: "" };

export default function JournalEntry() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [entries, setEntries] = useState([]);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [loading, setLoading] = useState(false);

  const loadAccounts = useCallback(() => {
    api.get("/accounting/chart")
      .then((r) => setAccounts((Array.isArray(r.data) ? r.data : []).filter((a) => a.IsActive)))
      .catch(() => setAccounts([]));
  }, []);

  const loadEntries = useCallback(() => {
    setLoading(true);
    api.get("/accounting/journal", { params: { from, to } })
      .then((r) => setEntries(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMsg("Failed to load journal entries"))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Account dropdown options, grouped by type (rendered as disabled headers).
  const accountMenu = () =>
    TYPE_ORDER.flatMap((type) => {
      const inType = accounts.filter((a) => a.AccountType === type);
      if (!inType.length) return [];
      return [
        <MenuItem key={`h-${type}`} disabled sx={{ opacity: 0.7, fontWeight: 700 }}>{type}</MenuItem>,
        ...inType.map((a) => (
          <MenuItem key={a.Account_ID} value={a.AccountName} sx={{ pl: a.ParentName ? 4 : 3 }}>
            {a.AccountName}
          </MenuItem>
        )),
      ];
    });

  const save = () => {
    if (!form.outAccount || !form.inAccount) { setMsg("Pick both accounts"); return; }
    if (form.outAccount === form.inAccount) { setMsg("The two accounts must be different"); return; }
    if (!(Number(form.amount) > 0)) { setMsg("Enter an amount"); return; }
    setSaving(true);
    api.post("/accounting/journal", {
      EntryDate: form.EntryDate,
      outAccount: form.outAccount,
      inAccount: form.inAccount,
      amount: Number(form.amount),
      memo: form.memo || null,
    })
      .then(() => {
        setMsg("Journal entry saved");
        setForm({ ...EMPTY, EntryDate: form.EntryDate });
        loadEntries();
      })
      .catch((e) => setMsg(e.response?.data?.error || "Save failed"))
      .finally(() => setSaving(false));
  };

  const remove = (id) => {
    if (!window.confirm("Delete this journal entry?")) return;
    api.delete(`/accounting/journal/${id}`)
      .then(() => { setMsg("Deleted"); loadEntries(); })
      .catch((e) => setMsg(e.response?.data?.error || "Delete failed"));
  };

  const noAccounts = accounts.length === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Journal Entry</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Record anything that isn't a purchase, sale, or daily cash — buying equipment, a loan payment,
        a credit-card charge, a bank transfer, owner money in/out, depreciation, or an adjustment.
      </Typography>

      {noAccounts && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your Chart of Accounts is empty. Go to <b>Accounting → Chart of Accounts</b> and click
          <b> Import my QuickBooks chart</b> first, so the accounts show up in the dropdowns below.
        </Alert>
      )}

      {/* Entry form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
            <TextField type="date" label="Date" size="small" InputLabelProps={{ shrink: true }}
              value={form.EntryDate} onChange={(e) => setForm({ ...form, EntryDate: e.target.value })} sx={{ minWidth: 160 }} />
            <TextField select label="Money OUT of" size="small" value={form.outAccount}
              onChange={(e) => setForm({ ...form, outAccount: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }} helperText="the account the money comes from">
              {accountMenu()}
            </TextField>
            <TextField select label="Money INTO" size="small" value={form.inAccount}
              onChange={(e) => setForm({ ...form, inAccount: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }} helperText="the account the money goes to">
              {accountMenu()}
            </TextField>
            <TextField type="number" label="Amount" size="small" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              inputProps={{ step: "0.01", min: "0" }} sx={{ width: 150 }} />
          </Stack>
          <TextField label="Memo (optional)" size="small" value={form.memo}
            onChange={(e) => setForm({ ...form, memo: e.target.value })}
            placeholder="e.g. Bought excavator on Chase card" />
          <Box>
            <Button variant="contained" onClick={save} disabled={saving || noAccounts}
              startIcon={saving ? <CircularProgress size={16} /> : null}>
              Save entry
            </Button>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          <b>Examples:</b> Bought excavator on Chase card → OUT of <i>Chase Credit Card</i>, INTO <i>Cost Basis (F.A)</i>. ·
          Paid the loan from Frost → OUT of <i>Frost Bank</i>, INTO <i>Develon Loan</i>. ·
          Owner put in cash → OUT of <i>Equity-Usman</i>, INTO <i>Cash in Register</i>.
        </Typography>
      </Paper>

      {/* Entry list */}
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>Journal Entries</Typography>
          <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)} />
        </Stack>

        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress /></Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", p: 2 }}>No journal entries in this range.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Debit (in)</TableCell>
                  <TableCell align="right">Credit (out)</TableCell>
                  <TableCell>Memo</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <React.Fragment key={e.Entry_ID}>
                    {e.lines.map((l, i) => (
                      <TableRow key={l.Line_ID} sx={{ "& td": { borderBottom: i === e.lines.length - 1 ? undefined : "none" } }}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{i === 0 ? String(e.EntryDate).slice(0, 10) : ""}</TableCell>
                        <TableCell>{l.AccountName}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{l.Debit > 0 ? fmt(l.Debit) : ""}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{l.Credit > 0 ? fmt(l.Credit) : ""}</TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>{i === 0 ? (e.Memo || "") : ""}</TableCell>
                        <TableCell align="right">
                          {i === 0 && (
                            <Tooltip title="Delete entry">
                              <IconButton size="small" color="error" onClick={() => remove(e.Entry_ID)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)} message={msg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}
